import { useState, type ReactNode } from "react";
import { Alert, Button, Skeleton, Textarea } from "@fellowflats/ui";
import { describe } from "../lib/use-api";

/** Loading, error and loaded states for one API read. */
export function Loaded<T>({ state, children }: { state: { data: T | null; error: string | null; loading: boolean }; children: (data: T) => ReactNode }) {
  if (state.error) return <Alert>{state.error}</Alert>;
  if (!state.data) return <Skeleton className="h-40 w-full" />;
  return <>{children(state.data)}</>;
}

/** A button that runs a staff action, shows it working, and reports a refusal in place. */
export function ActionButton({
  run,
  children,
  variant = "outline",
  confirm,
}: {
  run: () => Promise<unknown>;
  children: ReactNode;
  variant?: "primary" | "outline" | "danger" | "ghost";
  confirm?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <span className="inline-flex flex-col gap-1">
      <Button
        size="sm"
        variant={variant}
        loading={busy}
        onClick={async () => {
          if (confirm && !window.confirm(confirm)) return;
          setBusy(true);
          setError(null);
          try {
            await run();
          } catch (e) {
            setError(describe(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        {children}
      </Button>
      {error && <span className="text-xs text-danger">{error}</span>}
    </span>
  );
}

/** An action that needs a written reason (a rejection, a withdrawal). */
export function NotesAction({
  label,
  placeholder,
  required,
  run,
}: {
  label: string;
  placeholder: string;
  required?: boolean;
  run: (notes: string) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {label}
      </Button>
    );
  }
  return (
    <div className="w-full space-y-2 rounded-md border border-border p-3">
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={placeholder} rows={2} maxLength={500} />
      <div className="flex gap-2">
        <ActionButton variant="danger" run={() => (required && !notes.trim() ? Promise.reject(new Error("Write a short reason first.")) : run(notes.trim()))}>
          {label}
        </ActionButton>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export const day = (iso: string | null) =>
  iso ? new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "short", day: "numeric" }).format(new Date(iso)) : "-";
